import type { ChatMessage, PresentationContent, ReferenceSlideImage } from "../../../types";
import type { ModelConfig } from "../../../modelConfig";
import { buildApiUrl } from "../../../config";
import type {
  AiRequestOptions,
  ReferenceDeckExamples,
  StreamCallbacks,
  ToolExecutionPolicy,
} from "../types";
import { prepareRequestData, tryRepairTruncatedJson } from "../../../utils";
import { withCredentials } from "../../apiClient";
import { withPptxLangfuseMetadata } from "../../../langfuseTrace";
import { getSystemPrompt } from "../promptCatalog";
import { buildAgentTools, type AgentToolDefinition } from "../agentToolSchemas";
import { parseJsonText } from "../openaiResponseParsing";
import { normalizeBase64Payload, parseBase64DataUri } from "../../../utils/imagePayload";
import {
  appendSavedContextRule,
  fetchSavedContext,
  prependSavedContext,
} from "../../../../shared/api/finster/savedContext";
import {
  AnthropicStreamError,
  readAnthropicMessagesStream,
  type AnthropicTurnResult,
} from "../../../../shared/anthropic/messagesStream";
import {
  buildCachedSystem,
  withCachedToolDefinitions,
  withRollingCacheBreakpoint,
} from "../../../../shared/anthropic/promptCaching";
import { ADAPTIVE_THINKING, toAnthropicEffort } from "../../../../shared/anthropic/thinking";
import type { AgentEvent } from "../agentStream";
import type { NativeFunctionCall, NativeToolOutcome } from "../nativeAgent/toolCalls";
import { runNativeAgent, type NativeAgentSession } from "../nativeAgent/runNativeAgent";
import { sendNativeAgentRequest } from "../nativeAgent/transport";

type ContentBlock = Record<string, unknown>;

interface MessagesTurn {
  role: "user" | "assistant";
  content: ContentBlock[];
}

/** Used when the catalog does not name a cap for the selected Claude model. */
export const DEFAULT_CLAUDE_MAX_OUTPUT_TOKENS = 64_000;
/** Same per-hop cap as GPT's latest-screenshots message. */
export const MAX_SCREENSHOTS_PER_HOP = 16;
/** Vertex rejects request payloads over 30 MB; stay clear of that limit. */
export const MAX_CLAUDE_REQUEST_BYTES = 28 * 1024 * 1024;

const SCREENSHOT_OMITTED_NOTE =
  "Screenshot omitted to keep this request within the size limit. Rely on the slidesInfo returned above.";
const REFUSAL_ERROR = "Claude declined to complete this request.";
const CONTEXT_EXCEEDED_ERROR = "This conversation is too long for Claude to continue.";
const UNSUPPORTED_STOP_ERROR = "Claude stopped before finishing its answer.";
const REQUEST_TOO_LARGE_ERROR = "This request is too large to send to Claude.";

const encoder = new TextEncoder();

function byteLength(value: unknown): number {
  return encoder.encode(typeof value === "string" ? value : JSON.stringify(value)).byteLength;
}

/** FNV-1a over the encoded image: enough to tell an unchanged screenshot apart. */
function fingerprint(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${value.length}:${(hash >>> 0).toString(16)}`;
}

function toImageBlock(rawImage: string | undefined): ContentBlock | null {
  const parsed = parseBase64DataUri(rawImage);
  if (parsed) {
    return {
      type: "image",
      source: { type: "base64", media_type: parsed.mimeType, data: parsed.base64 },
    };
  }
  const base64 = normalizeBase64Payload(rawImage);
  return base64
    ? { type: "image", source: { type: "base64", media_type: "image/png", data: base64 } }
    : null;
}

/** Converts an agent tool definition to the Messages API tool shape (never `strict`). */
export function toAnthropicTool(tool: AgentToolDefinition): ContentBlock {
  return { name: tool.name, description: tool.description, input_schema: tool.parameters };
}

/**
 * The seed user message, in the same order as GPT's input: prelude texts
 * (Skills, saved context), leading slide images, the deck request, then the
 * reference examples with each slide's spec followed by its image.
 */
export function buildClaudeSeedContent(
  baseText: string,
  preludeUserMessages: readonly string[] | undefined,
  slideImages?: string[],
  referenceExamples?: ReferenceDeckExamples
): ContentBlock[] {
  const content: ContentBlock[] = [];
  for (const message of preludeUserMessages ?? []) {
    if (message.trim()) content.push({ type: "text", text: message });
  }
  for (const image of slideImages ?? []) {
    const block = toImageBlock(image);
    if (block) content.push(block);
  }
  content.push({ type: "text", text: baseText });
  if (referenceExamples && referenceExamples.slides.length > 0) {
    content.push({ type: "text", text: referenceExamples.intro });
    for (const slide of referenceExamples.slides) {
      const spec = [slide.caption, slide.json].filter(Boolean).join("\n");
      content.push({ type: "text", text: spec ? `${slide.label}\n${spec}` : slide.label });
      const block = slide.image ? toImageBlock(slide.image) : null;
      if (block) content.push(block);
    }
    content.push({ type: "text", text: referenceExamples.outro });
  }
  return content;
}

/**
 * Reads the terminal reply. The shared prompt asks for `{ "mode": "chat",
 * "reply": ... }`, but Vertex blocks structured outputs for Claude, so the
 * text is parsed leniently and plain prose becomes the reply.
 */
export function parseClaudeTerminalText(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const asObject = (value: unknown) =>
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  try {
    const parsed = asObject(parseJsonText(trimmed));
    if (parsed) return parsed;
  } catch {
    const repaired = tryRepairTruncatedJson(trimmed);
    if (repaired) {
      try {
        const parsed = asObject(parseJsonText(repaired));
        if (parsed) return parsed;
      } catch {
        // Fall through to the embedded-object and prose fallbacks.
      }
    }
  }
  const start = trimmed.indexOf("{");
  const end = trimmed.lastIndexOf("}");
  if (start >= 0 && end > start) {
    try {
      const parsed = asObject(JSON.parse(trimmed.slice(start, end + 1)));
      if (parsed && "mode" in parsed) return parsed;
    } catch {
      // Not an embedded object; treat the whole text as prose.
    }
  }
  return { mode: "chat", reply: trimmed };
}

function mapClaudeStreamEvent(
  event: Record<string, unknown>,
  hop: number,
  includeApplyEditArgsDeltas: boolean
): AgentEvent[] {
  const block = event.content_block as Record<string, unknown> | undefined;
  const delta = event.delta as Record<string, unknown> | undefined;
  if (event.type === "content_block_start" && block?.type === "tool_use") {
    if (typeof block.id === "string" && typeof block.name === "string") {
      return [{ kind: "tool_call", hop, callId: block.id, tool: block.name, status: "calling" }];
    }
    return [];
  }
  if (event.type !== "content_block_delta" || !delta) return [];
  if (delta.type === "thinking_delta" && typeof delta.thinking === "string" && delta.thinking) {
    return [{ kind: "reasoning_delta", hop, text: delta.thinking }];
  }
  if (delta.type === "text_delta" && typeof delta.text === "string" && delta.text) {
    return [{ kind: "output_delta", hop, text: delta.text }];
  }
  if (
    includeApplyEditArgsDeltas &&
    delta.type === "input_json_delta" &&
    typeof delta.partial_json === "string" &&
    delta.partial_json
  ) {
    return [{ kind: "apply_edit_args_delta", hop, text: delta.partial_json }];
  }
  return [];
}

function slideLabeler(presentationContent: PresentationContent): (slideId: string) => string {
  const numbers = new Map<string, number>();
  for (const slide of presentationContent.slides ?? []) {
    if (slide.id) numbers.set(slide.id, (slide.index ?? 0) + 1);
  }
  return (slideId) => {
    const number = numbers.get(slideId);
    return number ? `slide ${number}` : `slide ${slideId}`;
  };
}

/**
 * Messages API session for the native agent.
 *
 * Claude 5.5 binds every thinking block to the exact `system`, `tools`, and
 * earlier messages that produced it, so the transcript is strictly
 * append-only: assistant turns are stored exactly as streamed, `system` and
 * `tools` never change within a run, and screenshots are attached once,
 * inside the tool result that captured them, and never removed afterwards.
 */
export function createAnthropicSession(params: {
  model: ModelConfig;
  systemPrompt: string;
  tools: AgentToolDefinition[];
  seedContent: ContentBlock[];
  presentationContent: PresentationContent;
  callbacks: StreamCallbacks;
  options: AiRequestOptions;
}): NativeAgentSession {
  const { model, callbacks, options } = params;
  const includeApplyEditArgsDeltas = options.streamApplyEditSlideSummaries === true;
  const system = buildCachedSystem(params.systemPrompt);
  const tools = withCachedToolDefinitions(params.tools.map(toAnthropicTool));
  const maxTokens = model.maxOutputTokens ?? DEFAULT_CLAUDE_MAX_OUTPUT_TOKENS;
  const effort = toAnthropicEffort(options.reasoningEffort);
  const labelSlide = slideLabeler(params.presentationContent);

  const messages: MessagesTurn[] = [{ role: "user", content: params.seedContent }];
  const fixedBytes = byteLength(system) + byteLength(tools) + 2_048;
  let transcriptBytes = byteLength(messages);
  let pendingResults: ContentBlock[] = [];
  let unansweredCalls: NativeFunctionCall[] = [];
  let lastTurn: AnthropicTurnResult | null = null;
  const sentScreenshotBySlide = new Map<string, string>();

  const pushMessage = (message: MessagesTurn) => {
    messages.push(message);
    transcriptBytes += byteLength(message);
  };

  const onEventForHop = (hop: number) => {
    const onAgentEvent = callbacks.onAgentEvent;
    if (!onAgentEvent) return undefined;
    return (event: Record<string, unknown>) => {
      for (const mapped of mapClaudeStreamEvent(event, hop, includeApplyEditArgsDeltas)) {
        onAgentEvent(mapped);
      }
    };
  };

  const toToolResultBlock = (
    outcome: NativeToolOutcome,
    extra: ContentBlock[] = []
  ): ContentBlock => {
    const output = JSON.stringify(outcome.output);
    // The API rejects `is_error` on a result that carries non-text blocks. A
    // failed call that still captured screenshots keeps them, as GPT does; its
    // JSON already says `success: false` and carries the error.
    const hasImages = extra.some((block) => block.type !== "text");
    return {
      type: "tool_result",
      tool_use_id: outcome.callId,
      content: extra.length > 0 ? [{ type: "text", text: output }, ...extra] : output,
      ...(outcome.isError && !hasImages ? { is_error: true } : {}),
    };
  };

  /**
   * Picks which screenshots this hop attaches: the newest image per slide,
   * at most 16, skipping slides whose screenshot is unchanged since it was
   * last sent and any image that would push the request past the size limit.
   */
  const attachScreenshots = (
    outcomes: readonly NativeToolOutcome[]
  ): Map<string, ContentBlock[]> => {
    const newestBySlide = new Map<string, { callId: string; image: ReferenceSlideImage }>();
    for (const outcome of outcomes) {
      for (const image of outcome.referenceImages ?? []) {
        if (!image.slideId || !image.image) continue;
        newestBySlide.delete(image.slideId);
        newestBySlide.set(image.slideId, { callId: outcome.callId, image });
      }
    }
    const candidates = Array.from(newestBySlide.values());
    const kept = new Set(candidates.slice(-MAX_SCREENSHOTS_PER_HOP));
    const blocksByCall = new Map<string, ContentBlock[]>();
    const add = (callId: string, ...blocks: ContentBlock[]) =>
      blocksByCall.set(callId, [...(blocksByCall.get(callId) ?? []), ...blocks]);
    // This hop's tool-result JSON is sent alongside the images, so it counts too.
    let projectedBytes =
      fixedBytes +
      transcriptBytes +
      byteLength(pendingResults) +
      outcomes.reduce((total, outcome) => total + byteLength(toToolResultBlock(outcome)), 0);

    for (const candidate of candidates) {
      const { callId, image } = candidate;
      const label = labelSlide(image.slideId);
      if (!kept.has(candidate)) {
        add(callId, { type: "text", text: `Screenshot of ${label}: ${SCREENSHOT_OMITTED_NOTE}` });
        continue;
      }
      const hash = fingerprint(image.image);
      if (sentScreenshotBySlide.get(image.slideId) === hash) {
        add(callId, {
          type: "text",
          text: `Screenshot of ${label} unchanged since earlier in this turn.`,
        });
        continue;
      }
      const block = toImageBlock(image.image);
      if (!block) continue;
      const blockBytes = byteLength(block) + 128;
      if (projectedBytes + blockBytes > MAX_CLAUDE_REQUEST_BYTES) {
        add(callId, { type: "text", text: `Screenshot of ${label}: ${SCREENSHOT_OMITTED_NOTE}` });
        continue;
      }
      projectedBytes += blockBytes;
      sentScreenshotBySlide.set(image.slideId, hash);
      add(
        callId,
        { type: "text", text: `Rendered screenshot of ${label} after this tool call.` },
        block
      );
    }
    return blocksByCall;
  };

  return {
    provider: "anthropic",
    requestLabel: "ANTHROPIC-NATIVE",

    async sendTurn(request) {
      if (pendingResults.length > 0) {
        pushMessage({ role: "user", content: pendingResults });
        pendingResults = [];
      }
      const body = {
        model: model.model,
        system,
        messages: withRollingCacheBreakpoint(messages),
        tools,
        tool_choice: { type: request.toolChoice },
        max_tokens: maxTokens,
        thinking: ADAPTIVE_THINKING,
        output_config: { effort },
        stream: true,
      };
      const bodyJson = JSON.stringify(body);
      const requestBytes = encoder.encode(bodyJson).byteLength;
      if (requestBytes > MAX_CLAUDE_REQUEST_BYTES) {
        throw new Error(REQUEST_TOO_LARGE_ERROR);
      }
      const init = withCredentials({ method: "POST", body: bodyJson, signal: options.signal });
      const turn = await sendNativeAgentRequest({
        route: "/api/ai/anthropic/messages",
        url: buildApiUrl("/api/ai/anthropic/messages"),
        init: options.langfuseContext
          ? withPptxLangfuseMetadata(init, options.langfuseContext, {
              phase: request.phase,
              provider: "anthropic",
              model: model.model,
              label: request.label,
              reasoningEffort: effort,
              stream: true,
              toolCount: tools.length,
            })
          : init,
        requestBytes,
        requestLabel: request.label,
        retries: request.retries,
        signal: options.signal,
        readBody: (response) =>
          readAnthropicMessagesStream(response, {
            onEvent: onEventForHop(request.hop),
            // Truncation is tolerated like GPT's incomplete responses: a cut-off
            // tool input becomes a malformed-arguments result the model retries.
            rejectStopReasons: [],
          }),
        // Only explicit error frames are replayed. A network read failure or a
        // stream cut off before message_stop may be a WebView or proxy timeout,
        // and replaying it would multiply an already long wait.
        isTransientBodyError: (error) =>
          error instanceof AnthropicStreamError &&
          error.transient &&
          error.code !== "stream_read_failed" &&
          error.code !== "stream_ended_before_completion",
        onReplay: () => callbacks.onAgentEvent?.({ kind: "hop_reset", hop: request.hop }),
      });
      if (turn.stopReason === "refusal") throw new Error(REFUSAL_ERROR);
      if (turn.stopReason === "model_context_window_exceeded") {
        throw new Error(CONTEXT_EXCEEDED_ERROR);
      }
      if (turn.stopReason === "pause_turn") throw new Error(UNSUPPORTED_STOP_ERROR);

      lastTurn = turn;
      pushMessage({ role: "assistant", content: turn.raw as ContentBlock[] });
      unansweredCalls = turn.toolCalls.map((call) => ({
        callId: call.callId,
        name: call.name,
        rawArguments: call.arguments,
      }));
      return { calls: unansweredCalls, missingCallIdCount: 0, reasoning: turn.reasoning };
    },

    appendToolOutcomes(outcomes) {
      const screenshots = attachScreenshots(outcomes);
      for (const outcome of outcomes) {
        pendingResults.push(toToolResultBlock(outcome, screenshots.get(outcome.callId)));
      }
      const answered = new Set(outcomes.map((outcome) => outcome.callId));
      unansweredCalls = unansweredCalls.filter((call) => !answered.has(call.callId));
    },

    pendingCalls() {
      return { calls: unansweredCalls, missingCallIdCount: 0 };
    },

    terminalPayload() {
      return lastTurn ? parseClaudeTerminalText(lastTurn.text) : null;
    },

    toProviderResultItem(outcome) {
      const placeholders = (outcome.referenceImages ?? []).map((image) => ({
        type: "image",
        slideId: image.slideId,
      }));
      return { toolResultBlock: toToolResultBlock(outcome, placeholders) };
    },
  };
}

export async function queryAnthropic(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  model: ModelConfig,
  slideImages?: string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const savedContext = await fetchSavedContext(options.agentToolContext?.log);
  const runOptions: AiRequestOptions = savedContext
    ? {
        ...options,
        savedContext,
        preludeUserMessages: prependSavedContext(options.preludeUserMessages, savedContext),
      }
    : options;

  const { textMessage } = prepareRequestData(presentationContent, userQuery, chatHistory);
  const session = createAnthropicSession({
    model,
    systemPrompt: appendSavedContextRule(getSystemPrompt(toolPolicy), runOptions.savedContext),
    tools: buildAgentTools(toolPolicy),
    seedContent: buildClaudeSeedContent(
      textMessage,
      runOptions.preludeUserMessages,
      slideImages,
      runOptions.referenceExamples
    ),
    presentationContent,
    callbacks,
    options: runOptions,
  });
  await runNativeAgent({
    session,
    presentationContent,
    callbacks,
    toolPolicy,
    options: runOptions,
  });
}
